/**
 * AdUrlConfirmCard — Director chat question variant ``ad_url_confirm``.
 *
 * Surfaced by the ``import_product_from_url`` Director tool after a
 * successful scrape. Shows the user the hero image + name + descriptor
 * + source link so they can spot a bad scrape BEFORE spending sparks.
 *
 * Two CTAs:
 *   - "Looks right — render it" → answer = "confirm"
 *   - "Let me fix it"            → answer = "edit"
 *
 * The Director's system-prompt URL section turns "confirm" into a
 * direct ``create_ugc_proof_ad`` call with the metadata values, and
 * "edit" into "OK, please type the product name + descriptor".
 *
 * Build spec ref: docs/drafts/ai-ads-phase-0-1-build-spec.md §11
 * Backend builder: ``ads_tools.build_ad_url_confirm_question``
 */

import { api } from '@ponpon/api-client'
import type { EmbeddedQuestion } from '@console/hooks/useAgentChat'

interface AdUrlConfirmMeta {
  source_url?: string
  product_name?: string
  product_descriptor?: string
  hero_image_path?: string
  price_text?: string
  warnings?: string[]
}

function readMeta(question: EmbeddedQuestion): AdUrlConfirmMeta {
  const m = question.metadata
  if (!m) return {}
  return {
    source_url: m.source_url,
    product_name: m.product_name,
    product_descriptor: m.product_descriptor,
    hero_image_path: m.hero_image_path,
    price_text: m.price_text,
    warnings: m.warnings,
  }
}

function shortHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export function AdUrlConfirmCard({
  question,
  projectId,
  selected,
  readOnly,
  onAnswer,
}: {
  question: EmbeddedQuestion
  /** Required to render the hero preview — the hero_image_path is a
   *  project-relative asset path that needs the project context to
   *  resolve to a real GET URL. */
  projectId: string | undefined
  selected: string | undefined
  readOnly: boolean
  onAnswer: (value: string) => void
}) {
  const meta = readMeta(question)
  const heroUrl =
    projectId && meta.hero_image_path
      ? api.getAssetUrl(projectId, meta.hero_image_path)
      : undefined

  return (
    <div
      className="rounded-md border border-line-2 bg-bg-2/60 px-3.5 py-3"
      data-testid="ad-url-confirm-card"
    >
      {question.header && (
        <div className="t-mono text-[0.62rem] uppercase tracking-[0.18em] text-accent mb-1">
          {question.header}
        </div>
      )}
      <div className="text-text text-[0.92rem] leading-snug mb-2.5">
        {question.question}
      </div>

      <div className="flex gap-3 mb-2.5">
        {heroUrl ? (
          <img
            src={heroUrl}
            alt={meta.product_name ?? 'Product'}
            className="w-24 h-24 rounded-md object-cover border border-line-2 shrink-0"
            data-testid="ad-url-confirm-hero"
          />
        ) : (
          <div
            className="w-24 h-24 rounded-md border border-dashed border-line-2 bg-white/[0.02] flex items-center justify-center text-text-3 text-[0.7rem] shrink-0"
            data-testid="ad-url-confirm-hero-missing"
          >
            no image
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div
            className="text-text text-[0.95rem] leading-tight font-semibold truncate"
            data-testid="ad-url-confirm-name"
            title={meta.product_name}
          >
            {meta.product_name || '(no product name found)'}
          </div>
          {meta.product_descriptor && (
            <div
              className="mt-1 text-text-3 text-[0.82rem] leading-snug line-clamp-3"
              data-testid="ad-url-confirm-descriptor"
            >
              {meta.product_descriptor}
            </div>
          )}
          <div className="mt-1.5 flex items-center gap-2 text-text-3 text-[0.74rem] t-mono">
            {meta.price_text && (
              <span data-testid="ad-url-confirm-price">{meta.price_text}</span>
            )}
            {meta.price_text && meta.source_url && (
              <span aria-hidden>·</span>
            )}
            {meta.source_url && (
              <a
                href={meta.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-3 hover:text-text underline underline-offset-2"
                data-testid="ad-url-confirm-source"
              >
                {shortHost(meta.source_url)}
              </a>
            )}
          </div>
        </div>
      </div>

      {meta.warnings && meta.warnings.length > 0 && (
        <div
          className="mb-2.5 rounded-md border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2 text-amber-200 text-[0.78rem]"
          data-testid="ad-url-confirm-warnings"
        >
          <div className="font-semibold mb-0.5">
            Heads up
          </div>
          <ul className="list-disc list-inside space-y-0.5">
            {meta.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {question.options.map((opt, i) => {
          const value = opt.value ?? opt.label
          const isSelected = selected === value
          const isConfirm = value === 'confirm'
          return (
            <button
              key={`${value}-${i}`}
              type="button"
              disabled={readOnly}
              onClick={readOnly ? undefined : () => onAnswer(value)}
              data-testid={`ad-url-confirm-${value}`}
              className={`group text-left px-3.5 py-2.5 rounded-md border transition-all relative ${
                isSelected
                  ? 'border-accent bg-accent/[0.06] shadow-[0_0_0_1px_var(--accent)_inset]'
                  : readOnly
                    ? 'border-line-2 bg-white/[0.02] opacity-50 cursor-default'
                    : isConfirm
                      ? 'border-line-2 bg-accent/[0.04] hover:bg-accent/[0.08] hover:border-accent/60'
                      : 'border-line-2 bg-white/[0.02] hover:bg-white/[0.04] hover:border-line-3'
              }`}
            >
              <div
                className={`text-[0.95rem] leading-tight ${
                  isSelected ? 'text-accent' : 'text-text'
                }`}
                style={{
                  fontVariationSettings: '"wdth" 96, "opsz" 96',
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                }}
              >
                {opt.label}
              </div>
              {opt.description && (
                <div
                  className={`mt-0.5 text-[0.82rem] leading-snug ${
                    isSelected ? 'text-accent/70' : 'text-text-3'
                  }`}
                >
                  {opt.description}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
